import SheetSearchController from "../lib/sheet-search-controller";

/**
 * The reference sheet: find a player from the club, then say how matches with them usually go.
 *
 * Two panels in one sheet rather than iOS's sheet over a sheet, which on a page would be a modal
 * over a modal. Picking a player does not add them - it carries their id, name and picture into the
 * outcome panel's hidden fields and swaps the panels over, and the reference is only added once one
 * of that panel's three submit buttons is pressed. So the choice is still asked once, at selection
 * time, which is what lets the row on the step behind show the outcome as a fact rather than as
 * another control.
 *
 * The outcome labels and the question above them name the player, so they are rendered with `{0}`
 * left in and filled in here. Rendering three labels per search result server-side would mean
 * knowing who is about to be picked.
 */
const LABEL_PLACEHOLDER = "{0}";

export default class extends SheetSearchController {
  static override targets = [
    "outcomeLabel",
    "outcomePanel",
    "playerFirstName",
    "playerId",
    "playerImage",
    "playerName",
    "prompt",
    "searchField",
    "searchResults",
  ];

  declare readonly outcomeLabelTargets: HTMLElement[];
  declare readonly outcomePanelTarget: HTMLElement;
  declare readonly playerFirstNameTarget: HTMLInputElement;
  declare readonly playerIdTarget: HTMLInputElement;
  declare readonly playerImageTarget: HTMLInputElement;
  declare readonly playerNameTarget: HTMLInputElement;
  declare readonly promptTarget: HTMLElement;
  declare readonly searchFieldTarget: HTMLElement;
  declare readonly searchResultsTarget: HTMLElement;

  override onPick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const row = target.closest<HTMLElement>("[data-player-id]");
    if (row === null) {
      return;
    }

    const name = row.dataset.playerName ?? "";
    const firstName = row.dataset.playerFirstName ?? "";

    this.playerFirstNameTarget.value = firstName;
    this.playerIdTarget.value = row.dataset.playerId ?? "";
    this.playerImageTarget.value = row.dataset.playerImage ?? "";
    this.playerNameTarget.value = name;

    /* The same fallback every player row in this flow makes: a search result without a separate
       first name is drawn, and named, by its display name. */
    const spoken = firstName === "" ? name : firstName;

    this.fill(this.promptTarget, spoken);
    this.outcomeLabelTargets.forEach((label) => this.fill(label, spoken));

    this.showOutcomePanel(true);
  }

  /**
   * Back to the search panel whenever the sheet shuts.
   *
   * Bound to the dialog's own `close` event rather than to the Cancel button, because Escape and a
   * press on the backdrop close a <dialog> without going anywhere near Cancel - and a sheet
   * reopened on the outcome panel of a player somebody just backed out of is a sheet that lost its
   * place.
   */
  onClose(): void {
    this.showOutcomePanel(false);
    this.reset();
  }

  private showOutcomePanel(showing: boolean): void {
    this.outcomePanelTarget.hidden = !showing;
    this.searchFieldTarget.hidden = showing;
    this.searchResultsTarget.hidden = showing;
  }

  /**
   * The template stays on the element rather than being read back out of its own text, so filling
   * it in twice for two different players still names the second one.
   */
  private fill(element: HTMLElement, name: string): void {
    const template = element.dataset.labelTemplate ?? element.textContent ?? "";

    element.textContent = template.split(LABEL_PLACEHOLDER).join(name);
  }
}
